import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Link } from 'react-router-dom';
import { ShieldAlert, Phone, Mail, ArrowRight } from 'lucide-react';

const Section = ({ title, children }) => (
  <div>
    <h2 className="font-heading text-xl font-semibold text-text-primary mb-3">{title}</h2>
    <div className="text-text-muted leading-relaxed space-y-3">{children}</div>
    <hr className="border-[#E4E8EB] mt-6" />
  </div>
);

export default function SafeguardingPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFA]">
      <Header />

      {/* Hero */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ShieldAlert className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-4 leading-tight">
            Safeguarding Adults
          </h1>
          <p className="text-lg text-text-muted leading-relaxed">
            Protecting the people in our care is our highest priority. This page explains what safeguarding means and how to raise a concern.
          </p>
          <p className="text-sm text-text-muted mt-4">Last updated: June 2026</p>
        </div>
      </section>

      {/* Emergency banner */}
      <section className="bg-red-600 py-4">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-white text-sm font-semibold text-center">
            If someone is in immediate danger — call <strong>999</strong> now. Do not delay.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-[#E4E8EB] p-8 space-y-6">

            <Section title="Our commitment">
              <p>
                Osabea Healthcare Solutions Limited is committed to safeguarding and promoting the welfare of every adult in our care. We recognise that some adults are at greater risk of abuse, neglect or exploitation, and we take our responsibilities in this area extremely seriously.
              </p>
              <p>
                Our safeguarding practices are in accordance with:
              </p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li><strong>The Care Act 2014</strong> — which places a duty on local authorities to safeguard adults at risk</li>
                <li><strong>CQC Regulation 13</strong> — Safeguarding service users from abuse and improper treatment</li>
                <li><strong>The Mental Capacity Act 2005 and Deprivation of Liberty Safeguards (DoLS)</strong></li>
                <li><strong>The Human Rights Act 1998</strong></li>
                <li><strong>Kent and Medway Safeguarding Adults Board Procedures</strong></li>
              </ul>
            </Section>

            <Section title="What is safeguarding?">
              <p>
                Safeguarding means protecting an adult's right to live in safety, free from abuse and neglect. An "adult at risk" (previously known as a "vulnerable adult") is a person aged 18 or over who has care and support needs and is experiencing, or is at risk of, abuse or neglect, and as a result is unable to protect themselves.
              </p>
              <p>Types of abuse and neglect include (but are not limited to):</p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>Physical abuse</li>
                <li>Domestic violence</li>
                <li>Sexual abuse</li>
                <li>Psychological or emotional abuse</li>
                <li>Financial or material abuse</li>
                <li>Modern slavery and trafficking</li>
                <li>Discriminatory abuse</li>
                <li>Organisational or institutional abuse</li>
                <li>Neglect and acts of omission</li>
                <li>Self-neglect</li>
              </ul>
            </Section>

            <Section title="Our safeguarding procedures">
              <p>
                All Osabea Healthcare Solutions staff and workers are required to complete mandatory safeguarding adults training at a level appropriate to their role, and to keep that training up to date. We have a named <strong>Designated Safeguarding Lead (DSL)</strong> who is responsible for overseeing all safeguarding matters within our service.
              </p>
              <p>
                Any member of our team who has a safeguarding concern is required to report it immediately — without waiting for "proof" — to the DSL or directly to the local authority safeguarding team. We operate a <em>zero tolerance</em> policy towards abuse and neglect of any kind.
              </p>
              <p>
                We will always cooperate fully with any statutory safeguarding enquiry carried out by the local authority, the CQC, or the police.
              </p>
            </Section>

            <Section title="How to raise a safeguarding concern">
              <p>If you have a concern about the safety or welfare of a person receiving care from Osabea Healthcare Solutions — or any other person — you should:</p>
              <ol className="list-decimal list-inside space-y-2 mt-2">
                <li>
                  <strong>If there is immediate danger:</strong> Call <strong>999</strong> immediately.
                </li>
                <li>
                  <strong>Contact Osabea Healthcare Solutions directly:</strong> Speak to our Designated Safeguarding Lead on <a href="tel:01622809469" className="text-primary underline">01622 809469</a> or email <a href="mailto:info@osabeacares.co.uk" className="text-primary underline">info@osabeacares.co.uk</a>.
                </li>
                <li>
                  <strong>Contact Kent Adult Social Services:</strong> Kent County Council's Adult Social Services team can be reached on <a href="tel:03000416161" className="text-primary underline">03000 416161</a> (Monday–Friday, 9am–5pm) or out of hours on <a href="tel:03000419191" className="text-primary underline">03000 419191</a>.
                </li>
                <li>
                  <strong>Contact the CQC:</strong> You can report a concern about a regulated care service at <a href="https://www.cqc.org.uk/give-feedback-on-care" target="_blank" rel="noopener noreferrer" className="text-primary underline">cqc.org.uk</a> or by calling <strong>03000 616161</strong>.
                </li>
              </ol>
              <p>
                You do not need proof to raise a safeguarding concern. It is always better to report something and be wrong than to say nothing and leave someone at risk.
              </p>
            </Section>

            <Section title="Mental Capacity Act 2005">
              <p>
                Osabea Healthcare Solutions supports the principles of the Mental Capacity Act 2005. We assume that every adult has the capacity to make their own decisions unless it is established that they lack capacity. We support people to make their own decisions wherever possible.
              </p>
              <p>
                Where an individual lacks capacity to make a specific decision at a specific time, all decisions made on their behalf must be made in their best interests and must be the least restrictive option available.
              </p>
            </Section>

            <Section title="Our safeguarding team">
              <p>
                <strong>Designated Safeguarding Lead:</strong> Contact our office to speak with our DSL directly.<br />
                <strong>Telephone:</strong> <a href="tel:01622809469" className="text-primary underline">01622 809469</a><br />
                <strong>Email:</strong> <a href="mailto:info@osabeacares.co.uk" className="text-primary underline">info@osabeacares.co.uk</a>
              </p>
            </Section>

          </div>

          {/* Quick links */}
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <a href="https://www.cqc.org.uk/give-feedback-on-care" target="_blank" rel="noopener noreferrer" className="bg-white rounded-2xl border border-[#E4E8EB] p-6 hover:border-primary/40 transition-colors">
              <h3 className="font-semibold text-text-primary mb-1">Report to CQC</h3>
              <p className="text-sm text-text-muted mb-3">Give feedback or raise a concern about a care service directly with our regulator.</p>
              <span className="inline-flex items-center gap-1 text-sm text-primary font-medium">Visit CQC <ArrowRight className="h-3 w-3" /></span>
            </a>
            <Link to="/complaints" className="bg-white rounded-2xl border border-[#E4E8EB] p-6 hover:border-primary/40 transition-colors">
              <h3 className="font-semibold text-text-primary mb-1">Make a Complaint</h3>
              <p className="text-sm text-text-muted mb-3">Use our formal complaints procedure if you are unhappy with any aspect of our service.</p>
              <span className="inline-flex items-center gap-1 text-sm text-primary font-medium">Complaints Policy <ArrowRight className="h-3 w-3" /></span>
            </Link>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
